import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2 } from 'lucide-react';
import type { LeaseLine } from '../../../supabase/functions/_shared/lease-lines.ts';

export interface RentLineDraft {
  label: string;
  rent_ht: string;
  charges_ht: string;
  vat_rate: string;
}

export const VAT_OPTIONS = ['0', '5.5', '10', '20'];

export const emptyRentLine = (): RentLineDraft => ({ label: '', rent_ht: '', charges_ht: '0', vat_rate: '20' });

export function draftsFromLease(lease: any): { firstLabel: string; extras: RentLineDraft[] } {
  const lines = Array.isArray(lease?.rent_lines) ? lease.rent_lines : [];
  if (lines.length === 0) return { firstLabel: '', extras: [] };
  return {
    firstLabel: lines[0]?.label || '',
    extras: lines.slice(1).map((l: any) => ({
      label: l.label || '',
      rent_ht: String(l.rent_ht ?? ''),
      charges_ht: String(l.charges_ht ?? 0),
      vat_rate: String(l.vat_rate ?? 0),
    })),
  };
}

// Line 1 comes from the regular Loyer / Charges / Régime de TVA fields; extra lines are added with "+".
export function buildLeaseAmounts(formData: FormData, firstLabel: string, extras: RentLineDraft[]) {
  const first: LeaseLine = {
    label: firstLabel.trim(),
    rent_ht: parseFloat(formData.get('rent_amount') as string) || 0,
    charges_ht: parseFloat(formData.get('charges_amount') as string) || 0,
    vat_rate: parseFloat(formData.get('vat_rate') as string) || 0,
  };
  if (extras.length === 0) {
    return { rent_amount: first.rent_ht, charges_amount: first.charges_ht, vat_rate: first.vat_rate, rent_lines: null };
  }
  const lines: LeaseLine[] = [
    first,
    ...extras.map(l => ({
      label: l.label.trim(),
      rent_ht: parseFloat(l.rent_ht) || 0,
      charges_ht: parseFloat(l.charges_ht) || 0,
      vat_rate: parseFloat(l.vat_rate) || 0,
    })),
  ];
  const sum = (k: 'rent_ht' | 'charges_ht') => Math.round(lines.reduce((s, l) => s + l[k], 0) * 100) / 100;
  return { rent_amount: sum('rent_ht'), charges_amount: sum('charges_ht'), vat_rate: first.vat_rate, rent_lines: lines };
}

interface RentLinesEditorProps {
  firstLabel: string;
  onFirstLabelChange: (value: string) => void;
  lines: RentLineDraft[];
  onChange: (lines: RentLineDraft[]) => void;
}

export function RentLinesEditor({ firstLabel, onFirstLabelChange, lines, onChange }: RentLinesEditorProps) {
  const update = (index: number, patch: Partial<RentLineDraft>) =>
    onChange(lines.map((l, i) => (i === index ? { ...l, ...patch } : l)));

  return (
    <div className="space-y-3">
      {lines.length > 0 && (
        <div className="space-y-2">
          <Label htmlFor="first_line_label">Libellé de la ligne 1</Label>
          <Input
            id="first_line_label"
            value={firstLabel}
            onChange={(e) => onFirstLabelChange(e.target.value)}
            placeholder="Ex. Quote-part SCI FISTOUNE"
          />
          <p className="text-xs text-muted-foreground">La TVA de la ligne 1 est le « Régime de TVA » ci-dessous.</p>
        </div>
      )}

      {lines.map((line, index) => (
        <div key={index} className="rounded-lg border p-3 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Ligne {index + 2}</span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => onChange(lines.filter((_, i) => i !== index))}
              aria-label="Supprimer la ligne"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          <Input
            value={line.label}
            onChange={(e) => update(index, { label: e.target.value })}
            placeholder="Libellé (ex. Quote-part SCI MERINEL)"
          />
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Loyer (€) *</Label>
              <Input
                type="number"
                step="0.01"
                required
                value={line.rent_ht}
                onChange={(e) => update(index, { rent_ht: e.target.value })}
                placeholder="0.00"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Charges (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={line.charges_ht}
                onChange={(e) => update(index, { charges_ht: e.target.value })}
                placeholder="0.00"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">TVA</Label>
              <Select value={line.vat_rate} onValueChange={(v) => update(index, { vat_rate: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VAT_OPTIONS.map((r) => (
                    <SelectItem key={r} value={r}>{r === '0' ? 'Sans TVA (0%)' : `TVA ${r.replace('.', ',')}%`}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      ))}

      <Button type="button" variant="outline" size="sm" onClick={() => onChange([...lines, emptyRentLine()])}>
        <Plus className="h-4 w-4 mr-1" />
        Ajouter une ligne de loyer
      </Button>
    </div>
  );
}
